import React from 'react';
import { createLink, mapRoutesToItemsAndFindSelected, navigateToCallback } from '../components';
import { isPageRouteObject, isTabsRouteObject } from '../types';
/**
 * Custom React hook that manages global menu state, including open/close behavior,
 * dynamic menu items, badges, and selected menu item based on the current route.
 *
 * @param {NavigationInterface} navigationContext - The navigation context for route transitions.
 * @param {NavigateFunction} navigate - The navigate function from react-router for navigation actions.
 * @param {Location} location - The current browser location object.
 * @param {GlobalAppRouteObject[]} routes - Array of main application routes to map into menu items.
 * @param {string | undefined} basename - Optional base path to prefix all generated menu paths.
 * @param {GlobalAppRouteObject[]} [bottomMenuRoutes] - Optional array of bottom menu route objects.
 *
 * @returns {{
 *   setContextMenuItemBadges: React.Dispatch<React.SetStateAction<Record<string, number | string | undefined>>>;
 *   menuOpen: boolean;
 *   menuBadge: boolean;
 *   handleMenuOpen: (menuOpen: boolean) => void;
 *   menuItems: GlobalMenuItem[];
 *   bottomMenuItems: GlobalMenuItem[];
 *   selectedMenuItemId: string | undefined;
 * }} Object containing menu state, item lists, and handlers for interaction.
 */
export function useMenuState(navigationContext, navigate, location, routes, basename, bottomMenuRoutes) {
    const storageKey = `${basename ? basename : ''}.juvareGlobalMenuOpen`;
    const [contextMenuItemBadges, setContextMenuItemBadges] = React.useState({});
    const [menuOpen, setMenuOpen] = React.useState(window.localStorage.getItem(storageKey) === 'true');
    const handleMenuOpen = (menuOpen) => {
        window.localStorage.setItem(storageKey, '' + menuOpen);
        setMenuOpen(menuOpen);
    };
    const [menuItems, selectedMenuItemId] = mapRoutesToItemsAndFindSelected(createLink([basename !== null && basename !== void 0 ? basename : '/', location.pathname]), routes, (route) => routeToMenuItem(basename, contextMenuItemBadges, navigate, navigationContext, route));
    bottomMenuRoutes = bottomMenuRoutes || [];
    const [bottomMenuItems] = mapRoutesToItemsAndFindSelected(createLink([basename !== null && basename !== void 0 ? basename : '/', location.pathname]), bottomMenuRoutes, (route) => routeToMenuItem(basename, contextMenuItemBadges, navigate, navigationContext, route));
    const menuBadge = menuItems.findIndex((item) => item.badge != null) !== -1;
    return {
        setContextMenuItemBadges,
        menuOpen,
        menuBadge,
        handleMenuOpen,
        menuItems,
        bottomMenuItems,
        selectedMenuItemId,
    };
}
/**
 * Maps a single route object to a menu item configuration for use in the global navigation menu.
 *
 * @param {string | undefined} basename - Optional base path to prepend to generated paths.
 * @param {Record<string, number | string | undefined>} contextMenuItemBadges - Optional badges mapped by route path.
 * @param {NavigateFunction} navigate - React Router navigation function.
 * @param {NavigationInterface} navigationContext - Navigation context used to handle navigational behavior.
 * @param {GlobalAppRouteObject} route - The route object to be transformed into a menu item.
 *
 * @returns {GlobalMenuItem | undefined} A mapped menu item if applicable, or undefined if the route is not compatible.
 */
function routeToMenuItem(basename, contextMenuItemBadges, navigate, navigationContext, route) {
    var _a, _b, _c;
    if ((route.path || route.index) && (isPageRouteObject(route) || isTabsRouteObject(route))) {
        const path = (_a = route.path) !== null && _a !== void 0 ? _a : '';
        const handle = route.handle;
        const base = createLink([basename !== null && basename !== void 0 ? basename : '/', path]);
        const href = createLink([basename !== null && basename !== void 0 ? basename : '/', (_b = handle.navigateTo) !== null && _b !== void 0 ? _b : path]);
        const navigateTo = createLink([(_c = handle.navigateTo) !== null && _c !== void 0 ? _c : path]);
        const item = {
            id: path,
            icon: handle.icon,
            iconSelected: handle.iconSelected,
            label: handle.label,
            tooltip: handle.tooltip,
            badge: handle.badge,
            base: base,
            href: href,
            isSubMenuItem: handle.isSubMenuItem,
            disabled: handle.disabled,
            hidden: handle.hidden,
            onClick: navigateToCallback(navigationContext, navigate, navigateTo, handle.onClick),
            'data-test': handle['data-test'],
        };
        if (path in contextMenuItemBadges) {
            item.badge = contextMenuItemBadges[path];
        }
        return item;
    }
    else {
        return undefined;
    }
}
